import { useLocation } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import {
    List,
    Button,
    ListItem,
    Accordion,
    AccordionItem,
    AccordionButton,
    AccordionPanel,
    Divider,
    ListIcon,
    Text,
} from '@chakra-ui/react';

import { FormattedMessage } from 'react-intl';
import styled from '@emotion/styled/macro';

import { ReactComponent as DashboardIcon } from '../../../icons/dashboard.svg';
import { ReactComponent as PortfolioIcon } from '../../../icons/portfolio.svg';
import { ReactComponent as TurbineIcon } from '../../../icons/turbine.svg';

import ActiveLink from './ActiveLink';

import CustomNavLink from '../../utils/CustomNavLink';

const MobileNavigation = ({ userName, onClose }) => {
    const { instance } = useMsal();

    const location = useLocation();
    const showNav = !location.pathname.startsWith('/connection');

    return (
        <StyledList>
            {showNav && (
                <>
                    <ListItem>
                        <ActiveLink onClick={onClose} to="/" icon={DashboardIcon} messageId="common_dashboard" />
                    </ListItem>

                    <ListItem>
                        <ActiveLink
                            onClick={onClose}
                            to="/portfolios"
                            icon={PortfolioIcon}
                            messageId="portfolios_title"
                        />
                    </ListItem>

                    <ListItem>
                        <Accordion defaultIndex={[]} allowMultiple>
                            <AccordionItem border="none">
                                <StyledAccordionButton>
                                    <ListIcon as={TurbineIcon} />

                                    <FormattedMessage id="common_items" />
                                </StyledAccordionButton>

                                <AccordionPanel p={0}>
                                    <StyledList>
                                        <ListItem>
                                            <ActiveLink onClick={onClose} to="/loads" messageId="load_title" />
                                        </ListItem>

                                        <ListItem>
                                            <ActiveLink onClick={onClose} to="/batteries" messageId="batteries_title" />
                                        </ListItem>

                                        <ListItem>
                                            <ActiveLink onClick={onClose} to="/wind" messageId="wind_title" />
                                        </ListItem>

                                        {process.env.REACT_APP_SHOW_EXPERIMENTAL_FEATURES === 'true' && (
                                            <ListItem>
                                                <ActiveLink onClick={onClose} to="/solar" messageId="solar_title" />
                                            </ListItem>
                                        )}

                                        <ListItem>
                                            <ActiveLink
                                                onClick={onClose}
                                                to="/hedges-contracts"
                                                messageId="hedges_and_contracts_title"
                                            />
                                        </ListItem>
                                    </StyledList>
                                </AccordionPanel>
                            </AccordionItem>
                        </Accordion>
                    </ListItem>

                    <ListItem>
                        <Button as={CustomNavLink} to="/run-a-study" onClick={onClose}>
                            <FormattedMessage id="dashboard_run_a_study_btn" />
                        </Button>
                    </ListItem>

                    <ListItem>
                        <Divider my={4} />
                    </ListItem>
                </>
            )}

            <ListItem>
                <Text mb={3}>{userName}</Text>
            </ListItem>

            <ListItem>
                <ActiveLink to="/connection" onClick={onClose} messageId="connection_connection" />
            </ListItem>

            <ListItem>
                <StyledSignOutButton
                    className="sign-out-button"
                    onClick={() => instance.logoutRedirect()}
                    messageId="common_sign_out"
                />
            </ListItem>
        </StyledList>
    );
};

const StyledAccordionButton = styled(AccordionButton)`
    background-color: var(--chakra-colors-white);
    text-transform: capitalize;
    padding: 12px 0;
    border-radius: var(--chakra-radii-base);
    color: var(--chakra-colors-gray-600);
    font-weight: 500;

    svg {
        margin-right: 0.5rem;
        font-size: 1.5em; // adjust icon size based on font-size
        path {
            fill: var(--chakra-colors-gray-400);
        }
    }

    &:hover {
        background: none;
    }

    &[aria-expanded='true'] {
        p {
            color: var(--chakra-colors-black);
        }
        svg path {
            fill: var(--chakra-colors-blue-500);
        }
    }
`;

const StyledList = styled(List)`
    border-radius: var(--chakra-radii-base);

    li {
        a {
            display: flex;
            align-items: center;
            font-weight: 500;
            padding: 16px 0;
            color: var(--chakra-colors-gray-600);
            text-transform: capitalize;

            svg {
                font-size: 1.5em; // adjust icon size based on font-size
                path {
                    fill: var(--chakra-colors-gray-400);
                }
            }
        }

        .chakra-button {
            color: var(--chakra-colors-white);
            margin: 16px 0 28px;
        }

        ul {
            background: var(--chakra-colors-gray-100);
            padding: 0 30px;
            margin-top: 4px;
        }
    }
`;

const StyledSignOutButton = styled(ActiveLink)`
    color: var(--chakra-colors-gray-600) !important;
    margin: 0 !important;
    background: none;
    padding: 0;
    width: 100%;
    justify-content: start;
`;

export default MobileNavigation;
